import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  InformeCumplimientoRepository,
  EnvioInformeData,
  EnvioInformeResultado,
  PaginaInformesCumplimiento
} from 'src/app/domain/service/informe/informe-cumplimiento-repository';
import { ContactoInforme } from 'src/app/domain/model/informe/contacto-informe';
import { FiltroCumplimientos } from 'src/app/presentation/informes-rechazados/consultar-cumplimientos-form.component';

/**
 * Adaptador HTTP: HttpInformeCumplimientoRepository
 *
 * Implementa el puerto InformeCumplimientoRepository para comunicación HTTP.
 * Backend: GET /api/servicios/informes-cumplimiento/pendientes
 *
 * Legacy: FilterCumplimProgPresenter.java
 */
@Injectable({ providedIn: 'root' })
export class HttpInformeCumplimientoRepository implements InformeCumplimientoRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/informes-cumplimiento`;

  /**
   * Consulta una página de informes de cumplimiento pendientes.
   *
   * El backend valida y limita `size` a un máximo de 100 (ver
   * InformeCumplimientoController.MAX_PAGE_SIZE). page/size viajan reales al
   * servidor: paginación de servidor, no se trae de más para paginar en cliente.
   */
  async consultarPendientes(
    filtro: FiltroCumplimientos,
    page: number,
    size: number
  ): Promise<PaginaInformesCumplimiento> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (filtro.cdorden) {
      params = params.set('cdorden', filtro.cdorden);
    }
    if (filtro.nmconsecutivoCliente) {
      params = params.set('nmconsecutivoCliente', filtro.nmconsecutivoCliente.toString());
    }
    if (filtro.nmconsecutivoBeneficiario) {
      params = params.set('nmconsecutivoBeneficiario', filtro.nmconsecutivoBeneficiario.toString());
    }
    if (filtro.nmconsecutivoAsesor) {
      params = params.set('nmconsecutivoAsesor', filtro.nmconsecutivoAsesor.toString());
    }
    if (filtro.nmconsecutivoOficina) {
      params = params.set('nmconsecutivoOficina', filtro.nmconsecutivoOficina.toString());
    }
    if (filtro.nmconsecutivoUnidadAdmin) {
      params = params.set('nmconsecutivoUnidadAdmin', filtro.nmconsecutivoUnidadAdmin.toString());
    }
    if (filtro.feProgramacionIni) {
      params = params.set('feProgramacionIni', filtro.feProgramacionIni.toISOString());
    }
    if (filtro.feProgramacionFin) {
      params = params.set('feProgramacionFin', filtro.feProgramacionFin.toISOString());
    }
    if (filtro.snRiesgosProg) {
      params = params.set('snRiesgosProg', filtro.snRiesgosProg);
    }

    return firstValueFrom(
      this.http.get<PaginaInformesCumplimiento>(`${this.baseUrl}/pendientes`, { params })
    );
  }

  async enviarInforme(data: EnvioInformeData, archivo: File): Promise<EnvioInformeResultado> {
    const formData = new FormData();
    formData.append('archivo', archivo, archivo.name);
    formData.append(
      'data',
      new Blob([JSON.stringify(data)], { type: 'application/json' })
    );
    return firstValueFrom(
      this.http.post<EnvioInformeResultado>(`${this.baseUrl}/enviar`, formData)
    );
  }

  async gestionarContactos(nmconsecutivoInforme: number, cumplimientos: string): Promise<ContactoInforme[]> {
    const params = new HttpParams().set('cumplimientos', cumplimientos);
    return firstValueFrom(
      this.http.post<ContactoInforme[]>(`${this.baseUrl}/${nmconsecutivoInforme}/contactos/gestionar`, null, { params })
    );
  }

  async listarContactos(nmconsecutivoInforme: number): Promise<ContactoInforme[]> {
    return firstValueFrom(
      this.http.get<ContactoInforme[]>(`${this.baseUrl}/${nmconsecutivoInforme}/contactos`)
    );
  }

  async crearContactos(nmconsecutivoInforme: number, contactos: ContactoInforme[]): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.baseUrl}/${nmconsecutivoInforme}/contactos`, contactos)
    );
  }

  async actualizarContacto(nmconsecutivoInforme: number, contacto: ContactoInforme): Promise<void> {
    return firstValueFrom(
      this.http.put<void>(`${this.baseUrl}/${nmconsecutivoInforme}/contactos/${contacto.nmconsecutivo}`, contacto)
    );
  }

  async eliminarContacto(nmconsecutivoInforme: number, nmconsecutivo: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${this.baseUrl}/${nmconsecutivoInforme}/contactos/${nmconsecutivo}`)
    );
  }
}
